import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import TableComponent from "components/grid";
import AddRisk from "./AddRisk";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { useNavigate, useParams } from "react-router-dom";
import EditRisk from "./EditRisk";
import { useGetAllRiskQuery } from "store/slices/apis/riskPackageSlice";
import moment from "moment";
import { useDeleteKeyRiskMutation } from "store/slices/apis/keyRiskSlice";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { RISK_VIEW, RISK_CREATE, RISK_EDIT, RISK_DELETE } from "config";
import { usePermission } from "hooks/usePermission";
import { ReactComponent as AddNewIcon } from "assets/PwC_Funct_Icons_Plus_Outline_Black_RGB.svg"
import { ReactComponent as SearchIcon } from "assets/PwC_Funct_Icons_Search_Outline_Black_RGB.svg"
import { ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"
import { ReactComponent as CalendarIcon } from "assets/PwC_Funct_Icons_Calendar_Outline_Black_RGB.svg"

const columns = [
  {
    name: "Risk ID",
    field: "risk_number",
    renderer(params) {
      return (
        <Stack direction="row" spacing={1}>
          {params?.risk_number && (
            <Chip
              label={params?.risk_number}
              size="small"
              sx={{
                fontSize: "12px",
                "& .MuiChip-label": {
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  fontWeight: 600,
                },
              }}
            />
          )}
        </Stack>
      );
    },
  },
  {
    name: "Consultant/Contractor",
    field: "contrctorname",
  },
  {
    name: "Category",
    field: "package",
  },
  {
    name: "Sub Category 1",
    field: "sub_package_1",
  },
  {
    name: "Sub Category 2",
    field: "sub_package_2",
  },
  {
    name: "Workflow Status",
    field: "approved_status",
    renderer(params) {
      return (
        <>
          {params?.approved_status === "Pending_for_Approval"
            ? "Pending for approval"
            : params?.approved_status}
        </>
      );
    },
  },
  {
    name: "Current Status",
    field: "status",
  },
  {
    name: "Created Date",
    field: "createddate",
    renderer(params) {
      return (
        <>
          {params?.createddate
            ? moment(params.createddate).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Risk Category",
    field: "risk_category",
  },
  {
    name: "Risk Reporting Date",
    field: "risk_reporting_date",
    renderer(params) {
      return (
        <>
          {params?.risk_reporting_date
            ? moment(params.risk_reporting_date).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Likelihood Score",
    field: "likelihood_score",
  },
  {
    name: "Impact Score Quality",
    field: "impact_score_quality",
  },
  {
    name: "Impact Score Cost",
    field: "impact_score_cost",
  },
  {
    name: "Impact Score Overall",
    field: "impact_score_overall",
  },
  {
    name: "Risk Score",
    field: "risk_score",
  },
  {
    name: "Severity",
    field: "severity",
  },
  {
    name: "Action",
    field: "action",
    option: {},
  },
];

const approvedStatuses = [
  { label: "Pending for approval", value: "Pending_for_Approval" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

const AllRisk = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const viewAccess = usePermission(RISK_VIEW);
  const addAccess = usePermission(RISK_CREATE);
  const editAccess = usePermission(RISK_EDIT);
  const deleteAccess = usePermission(RISK_DELETE);

  const [showAddIssue, setShowAddIssue] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [isEditIssueModal, setEditIssueModal] = useState(false);
  const [issueData, setEditIssueData] = useState(null);
  const [isViewIssueModal, setIsViewIssueModal] = useState(false);
  const [approvedStatus, setApprovedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filters, setFilters] = useState({
    approvedStatus: "",
    fromDate: "",
    toDate: "",
  });
  const { projectId } = useParams();
  const {
    data: allRisk,
    refetch,
    isLoading,
  } = useGetAllRiskQuery({
    ProjectId: projectId,
    sortBy: "createddate",
    sortOrder: "desc",
    approved_status: "Approved",
    from: filters?.fromDate,
    to: filters?.toDate,
    page: pageNumber,
    pageSize: 10,
    // approved_status: filters?.approvedStatus,
  });
  const [issueDeleteFn, issueDeleteRes] = useDeleteKeyRiskMutation();

  const handleAddIssue = () => {
    setShowAddIssue(!showAddIssue);
  };

  const CustomCalendarIcon = (props) => (
    <CalendarIcon {...props} style={{ width: 26, height: 26, fill: "#696969 ", stroke: "#696969", strokeWidth: 4 }} />
  );

  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleActionEdit = (row) => {
    setEditIssueModal(!isEditIssueModal);
    if (!issueData) {
      setEditIssueData(row);
    } else {
      setEditIssueData(null);
    }
  };

  const encrypt = (text) => btoa(unescape(encodeURIComponent(text)));

  const handleActionView = (row) => {

    const encrypted = encrypt("view");
    navigate(`/projects/${projectId}/risk-detail/${row?.risk_id}/${encrypted}/false`);
    // setIsViewIssueModal(!isViewIssueModal);
    // if (!issueData) {
    //   setEditIssueData(row);
    // } else {
    //   setEditIssueData(null);
    // }
  };
  const handleActionDelete = (row) => {
    setDeleteItem(row);
    setDeleteModal(true);
  };

  const handleConfirmationDelete = () => {
    issueDeleteFn(deleteItem?.risk_id);
  };

  useEffect(() => {
    if (issueDeleteRes?.isSuccess) {
      if (issueDeleteRes?.data?.status == "success") {
        openNotification(issueDeleteRes?.data?.message, "success");
        refetch();
      } else {
        openNotification(issueDeleteRes?.data?.message, "error");
      }
      handleCloseDelete();
      refetch();
    }
  }, [issueDeleteRes?.isSuccess]);

  const handleCloseDelete = () => {
    setDeleteItem(null);
    setDeleteModal(false);
  };

  // const processedColumns = columns.map((col) => {
  //   if (col.field === "action") {
  //     console.log(allRisk);

  //     const deleteOrEdit = allRisk?.data?.some(
  //       (risk) => risk.status?.toLowerCase() !== "open"
  //     )
  //     console.log(deleteOrEdit);

  //     return {
  //       ...col,
  //       option: {
  //         ...col.option,
  //         delete: deleteOrEdit,
  //         edit: deleteOrEdit,
  //       },
  //     };
  //   }
  //   return col;
  // });

  const confirmationDeleteDialog = () => (
    <Dialog open={isDeleteModal} aria-labelledby="draggable-dialog-title">
      <DialogTitle
        sx={{
          fontWeight: 500,
          fontSize: "20px",
          color: "#252525",
          paddingBottom: 0,
        }}
        id="draggable-dialog-title"
      >
        Delete
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCloseDelete}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        {/* <IoMdClose style={{ fontSize: "20px" }} /> */}
        <CrossIcon style={{ fontSize: "20px", width: "20px", height: "20px", fill: "#808080 " }} />
      </IconButton>
      <DialogContent>
        <DialogContentText
          sx={{ fontWeight: 400, fontSize: "16px", color: "#474747" }}
        >
          Are you sure you want to Delete this risk? This action is permanent.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleCloseDelete}
          sx={{
            color: "#415385",
            fontSize: "16px",
            fontWeight: "500",
            textTransform: "none",
            border: "1px solid #415385",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmationDelete}
          loading={issueDeleteRes?.isLoading}
          variant="contained"
          sx={{
            backgroundColor: "#C52A1A",
            color: "#fff",
            fontSize: "16px",
            fontWeight: "500",
            textTransform: "none",
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {confirmationDeleteDialog()}
      <Grid2 container spacing={2} sx={{ mt: "20px" }}>
        <Grid2 size={12}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{
                flexGrow: 1,
                flexWrap: 'wrap',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' },
                "& .MuiStack-root": {
                  paddingTop: "15px",
                },
              }}>
              {/* <Autocomplete
                                    options={approvedStatuses}
                                    getOptionLabel={(option) => option?.label}
                                    onChange={(_, newValue) =>
                                      setApprovedStatus(newValue ? newValue?.value : "")
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Current Status"
                                        name="approvedStatus"
                                        // slotProps={{ inputLabel: { shrink: true } }}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          "& .MuiFilledInput-root": {
                                            backgroundColor: "#fff",
                                          },
                    
                                          "& .MuiInputLabel-root": {
                                            top: "-3px",
                                          },                    
                                          minWidth: "230px",
                                        }}
                                      />
                                    )}
                                    ListboxProps={{
                                      sx: { fontSize: "13px" },
                                    }}
                                    // sx={{ width: "23%", marginTop: "7px !important" }}
                                  /> */}
              <LocalizationProvider dateAdapter={AdapterDayjs} size="small">
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    format="DD-MM-YYYY"
                    onChange={(newValue) => setFromDate(newValue)}
                    slots={{
                      openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: "",
                            toDate: toDate
                              ? dayjs(toDate).format("YYYY-MM-DD")
                              : "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    format="DD-MM-YYYY"
                    clearable={true}
                    onChange={(newValue) => setToDate(newValue)}
                    slots={{
                      openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: fromDate
                              ? dayjs(fromDate).format("YYYY-MM-DD")
                              : "",
                            toDate: "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFilters({
                    fromDate: fromDate
                      ? dayjs(fromDate).format("YYYY-MM-DD")
                      : "",
                    toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
                    approvedStatus,
                  });
                }}
                size="large"
                //  startIcon={<IoMdSearch />}
                startIcon={<SearchIcon style={{ height: "20px", width: "20px", fill: `${theme?.customSettings?.themeColor}` }} />}
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 20px",
                  marginTop: "12px !important",
                }}
              >
                Search
              </Button>
            </Stack>
            {addAccess &&
              <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="contained"
                  onClick={handleAddIssue}
                  startIcon={<AddNewIcon style={{ height: "20px", width: "20px", fill: "#FFFFFF" }} />}
                  sx={{
                    backgroundColor: `${theme?.customSettings?.themeColor}`,
                    color: "#FFF",
                    padding: "5px 20px",
                    fontWeight: "500",
                    textTransform: "none",
                    fontSize: "16px",
                    //marginTop: "11px",
                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                    width: { xs: '100%', sm: 'auto' },
                    mt: { xs: 2, md: 1 },
                  }}
                >
                  Add Risk
                </Button>
              </Box>
            }

          </Box>
        </Grid2>

        <Grid2 size={12} sx={{ mt: 2 }}>
          <TableComponent
            columns={columns
              .map((col) =>
                col.field === "action"
                  ? {
                    ...col,
                    option: {
                      ...col.option,
                      view: viewAccess,
                      edit: editAccess,
                      delete: deleteAccess
                    },
                  }
                  : col
              )
              .filter((col) => {
                if (col.field !== "action") {
                  return true;
                } else {
                  return Object.values(col.option).some((v) => v === true);
                }

              })}
            data={allRisk?.data?.filter((risk) => risk?.active === true)}
            actionDelete={handleActionDelete}
            actionEdit={handleActionEdit}
            actionView={handleActionView}
            isLoading={isLoading}
          />
          {!isLoading && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <PaginationComponent
                pageNumber={allRisk?.pagination?.currentPage || 1}
                // totalPages={
                //   Math.ceil(
                //     allRisk?.pagination?.totalItems / allRisk?.pagination?.pageSize
                //   ) || 1
                // }
                totalPages={allRisk?.pagination?.totalPages || 1}
                onPageChange={setPageNumber}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>
      {showAddIssue && (
        <AddRisk
          cancelIssue={handleAddIssue}
          open={showAddIssue}
          openNotification={openNotification}
          refetch={refetch}
        />
      )}
      {isEditIssueModal && (
        <EditRisk
          cancelIssue={handleActionEdit}
          open={isEditIssueModal}
          data={issueData}
          openNotification={openNotification}
          refetch={refetch}
        />
      )}

      {isViewIssueModal && (
        <EditRisk
          cancelIssue={handleActionView}
          open={isViewIssueModal}
          data={issueData}
          openNotification={openNotification}
          isViewIssueModal
        />
      )}
    </>
  );
};

export default AllRisk;
